import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';

import { CampaignCreationCardHeader } from '@/components/glitch-ui/campaigncreationcardheader';

import normalizeUrl from 'normalize-url';
import { BusinessGenerateLoading } from './business-generate-loading';
import { useGenerateBusinessDescriptionMutation } from '@/lib/query-options';
import { motion } from 'framer-motion';
interface CampaignUrlProps {
  title: string;
  subTitle: string;
  loadingTitle: string;
  loadingSubTitle: string;
  businessUrl: string;
  setBusinessUrl: (url: string) => void;
  setBusinessDescription: (description: string) => void;
  setBusinessName?: (businessName: string) => void;
  onNext: () => void;
  setStepValid: (valid: boolean) => void;
  showInternalContinueButton?: boolean;
  // New props for merged brand name input
  brandName?: string;
  setBrandName?: (name: string) => void;
  showBrandNameInput?: boolean;
  /**
   * Tell-on-click validation (#5857): per-field errors set by the parent wizard when its
   * Continue button is clicked with missing/invalid input. The parent clears each field's
   * error as soon as that field is edited (via its setBrandName / setBusinessUrl wrappers).
   */
  fieldErrors?: { brandName?: string; businessUrl?: string };
}

export interface CampaignUrlRef {
  processUrl: () => Promise<boolean>;
}

export const CampaignUrl = forwardRef<CampaignUrlRef, CampaignUrlProps>(({
  title,
  subTitle,
  businessUrl,
  setBusinessUrl,
  setBusinessDescription,
  setBusinessName,
  onNext,
  setStepValid,
  showInternalContinueButton = true,
  brandName = '',
  setBrandName,
  showBrandNameInput = false,
  fieldErrors,
}, ref) => {
  const posthog = usePostHog();
  // Use parent URL directly (controlled component) - no local URL state
  const [error, setError] = useState<string | null>(null);
  const [brandNameError, setBrandNameError] = useState<string | null>(null);
  const [isStepValid, setIsStepValidInternal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const brandNameInputRef = useRef<HTMLInputElement>(null);

  const generateBusinessMutation = useGenerateBusinessDescriptionMutation();

  // Validate URL and brand name (if shown) and set step validity
  useEffect(() => {
    const isValidUrl = businessUrl && businessUrl.trim() !== '' && businessUrl !== 'https://';
    const isValidBrandName = !showBrandNameInput || (brandName && brandName.trim().length >= 2 && brandName.trim().length <= 100);
    const valid = Boolean(isValidUrl && isValidBrandName);
    setIsStepValidInternal(valid);
    setStepValid(valid);
  }, [businessUrl, brandName, showBrandNameInput, setStepValid]);

  const processUrl = async (): Promise<boolean> => {
    // Validate brand name if showing input
    if (showBrandNameInput) {
      if (!brandName || brandName.trim().length < 2) {
        setBrandNameError('Brand name must be at least 2 characters');
        posthog?.capture('onboarding_brand_name_validation_failed', {
          error: 'name_too_short',
          brand_name_length: brandName?.length || 0,
        });
        return false;
      }
      if (brandName.trim().length > 100) {
        setBrandNameError('Brand name must be 100 characters or less');
        posthog?.capture('onboarding_brand_name_validation_failed', {
          error: 'name_too_long',
          brand_name_length: brandName.trim().length,
        });
        return false;
      }
      setBrandNameError(null);
    }

    // Track validation start
    posthog?.capture('onboarding_url_validation_started', {
      raw_url: businessUrl,
      url_length: businessUrl?.length || 0,
      brand_name: showBrandNameInput ? brandName : undefined,
    });

    if (!businessUrl) {
      setError('Please check the website address and try again.');
      posthog?.capture('onboarding_url_validation_failed', {
        error_type: 'empty_url',
        raw_url: businessUrl,
      });
      return false;
    }

    // Basic URL cleanup
    let cleanUrl = businessUrl.trim();

    // Remove common prefixes if accidentally duplicated
    cleanUrl = cleanUrl.replace(/^(https?:\/\/)+/, '');
    // Remove the line that strips www prefixes - let backend handle this
    // cleanUrl = cleanUrl.replace(/^(www\.)+/, '');

    // Ensure single https:// prefix
    if (!cleanUrl.startsWith('http')) {
      cleanUrl = 'https://' + cleanUrl;
    }

    // Basic URL validation before attempting to normalize
    try {
      new URL(cleanUrl);
    } catch {
      setError('Please check the website address and try again.');
      posthog?.capture('onboarding_url_validation_failed', {
        error_type: 'invalid_url_format',
        raw_url: businessUrl,
        cleaned_url: cleanUrl,
      });
      return false;
    }

    try {
      // Further normalize the URL
      const normalizedUrl = normalizeUrl(cleanUrl, {
        defaultProtocol: 'https',
        stripWWW: false,
        removeTrailingSlash: false, // Let backend handle trailing slashes
        removeQueryParameters: true,
        removeSingleSlash: false,
        removeDirectoryIndex: true,
      });

      setBusinessUrl(normalizedUrl); // Update the displayed URL

      // Attempt to generate business description
      const data = await generateBusinessMutation.mutateAsync({ url: normalizedUrl });
      setBusinessUrl(data.url); // Use the validated URL from backend
      setBusinessDescription(data.description);
      // Use the user-provided brand name if showing input, otherwise use AI-extracted name
      if (showBrandNameInput && brandName) {
        if (setBusinessName) setBusinessName(brandName);
      } else if (setBusinessName) {
        setBusinessName(data.business_name);
      }
      setError(null);

      // Track successful validation
      posthog?.capture('onboarding_url_validation_success', {
        raw_url: businessUrl,
        normalized_url: normalizedUrl,
        final_url: data.url,
        has_business_name: Boolean(data.business_name),
        description_length: data.description?.length || 0,
      });

      return true;
    } catch (error: unknown) {
      console.error('Error processing URL:', error);

      // Extract HTTP status with proper type checking
      const httpStatus = error && typeof error === 'object' && 'response' in error &&
          typeof (error as { response?: { status?: number } }).response === 'object'
          ? (error as { response: { status?: number } }).response.status
          : undefined;

      const errorType = httpStatus === 400 ? 'website_inaccessible' : 'unknown_error';
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Track validation failure
      posthog?.capture('onboarding_url_validation_failed', {
        error_type: errorType,
        error_message: errorMessage,
        raw_url: businessUrl,
        http_status: httpStatus,
      });

      if (httpStatus === 400) {
        setError(
          'Unable to access this website. Please check:' +
            '\n1. The URL is spelled correctly' +
            '\n2. The website is online and publicly accessible' +
            '\n3. The website loads in a normal browser window',
        );
      } else {
        setError('Please check the website address and try again.');
      }
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    processUrl
  }));

  const handleContinue = async () => {
    const success = await processUrl();
    if (success) {
      onNext();
    }
  };

  return (
    <motion.div
      className='set-campaign-url flex-row space-y-6 !mt-0 aligns-center'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {generateBusinessMutation.isPending ? (
        <BusinessGenerateLoading
          showBackButton={true}
          onBackClick={() => {
            // Reset the mutation state and allow user to try again or go back
            generateBusinessMutation.reset();
            setError(null);
          }}
          backButtonText="Cancel"
        />
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <CampaignCreationCardHeader title={title} subTitle={subTitle} />
          </motion.div>
          <motion.div
            className='space-y-4 mx-6'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            onAnimationComplete={() => {
              if (showBrandNameInput) {
                brandNameInputRef.current?.focus();
              } else {
                inputRef.current?.focus();
              }
            }}
          >
            {showBrandNameInput && (
              <div className='space-y-2'>
                <div className='flex justify-between items-center'>
                  <label className='text-sm font-medium text-gray-700'>Business or Brand Name</label>
                  <FieldError message={fieldErrors?.brandName} />
                </div>
                <div className='relative'>
                  <Input
                    ref={brandNameInputRef}
                    placeholder='Your Business Name'
                    value={brandName}
                    onChange={(e) => setBrandName?.(e.target.value)}
                    error={!!fieldErrors?.brandName}
                    className={`!text-base !ring-inset !ring-transparent h-[54px]${fieldErrors?.brandName ? ' pr-8' : ''}`}
                  />
                  <InputAdornment error={!!fieldErrors?.brandName} />
                </div>
                <div className='flex justify-between items-center'>
                  {brandNameError ? <p className='text-sm text-destructive'>{brandNameError}</p> : <span />}
                  <span className={`text-xs ${(brandName?.trim().length || 0) > 100 ? 'text-destructive' : 'text-gray-500'}`}>
                    {brandName?.trim().length || 0}/100
                  </span>
                </div>
              </div>
            )}
            <div className='space-y-2'>
              {(showBrandNameInput || fieldErrors?.businessUrl) && (
                <div className='flex justify-between items-center'>
                  {showBrandNameInput ? <label className='text-sm font-medium text-gray-700'>Website</label> : <span />}
                  <FieldError message={fieldErrors?.businessUrl} />
                </div>
              )}
              <div className='relative'>
                <Input
                  ref={inputRef}
                  placeholder='tryglitch.ai'
                  value={businessUrl}
                  onChange={(e) => setBusinessUrl(e.target.value)}
                  error={!!fieldErrors?.businessUrl}
                  className={`!text-base !ring-inset !ring-transparent h-[54px]${fieldErrors?.businessUrl ? ' pr-8' : ''}`}
                />
                <InputAdornment error={!!fieldErrors?.businessUrl} />
              </div>
              {error && <p className='text-sm text-destructive'>{error}</p>}
            </div>
          </motion.div>
          {showInternalContinueButton && (
            <motion.div
              className='flex justify-end mx-6 pt-4'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Button
                onClick={handleContinue}
                disabled={!isStepValid || generateBusinessMutation.isPending}
                className='bg-[#290BD9] hover:bg-[#2309C5]'
              >
                Continue
              </Button>
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  );
});
